import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { QrCode } from 'lucide-react'

import { useQueryClient } from '@tanstack/react-query'
import type { AvailableCard } from '@/components/driver/available-order-card'
import { Button } from '@/components/ui/button'
import { orderKeys, useSalesOrders } from '@/hooks/use-sales-order-query'
import { useContractDeliveries } from '@/hooks/use-contract-delivery-query'
import { QRScannerDialog } from '@/components/sections/qr-scanner-dialog'
import { AvailableOrderCard } from '@/components/driver/available-order-card'
import { m } from '@/lib/paraglide/messages'
import { formatVnd } from '@/lib/format'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/driver/available-orders',
)({
    component: DriverAvailableOrdersPage,
})

function DriverAvailableOrdersPage() {
    const [qrScanOpen, setQrScanOpen] = useState(false)
    const qc = useQueryClient()

    const {
        data,
        isLoading: salesLoading,
        refetch,
    } = useSalesOrders({
        status: 'stock_out',
        per_page: 50,
    })
    const { data: cdoData, isLoading: cdoLoading } = useContractDeliveries({
        status: 'confirmed',
        per_page: 50,
    })

    const isLoading = salesLoading || cdoLoading

    const saleCards: AvailableCard[] = (data?.data ?? [])
        .filter((o) => !o.driver_assignee_id)
        .map((o) => ({
            kind: 'sale',
            id: o.id,
            code: o.order_code,
            recipientName: o.customer_name,
            phone: o.customer_phone,
            address: o.shipping_address,
            amountLabel:
                o.payment_status === 'paid'
                    ? formatVnd(o.total_amount)
                    : `${m.driver_order_cod_label()}: ${formatVnd(o.total_amount)}`,
        }))

    const contractCards: AvailableCard[] = (cdoData?.data ?? [])
        .filter((o) => !o.driver_assignee_id)
        .map((o) => ({
            kind: 'contract',
            id: o.id,
            code: o.order_code,
            recipientName: o.recipient_name,
            phone: o.recipient_phone,
            address: o.shipping_address,
        }))

    const cards = [...saleCards, ...contractCards]

    const handlePickupComplete = () => {
        void refetch()
        void qc.invalidateQueries({ queryKey: orderKeys.lists() })
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">
                        {m.driver_waiting_orders()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.driver_order_subtitle()}
                    </p>
                </div>
                <Button onClick={() => setQrScanOpen(true)}>
                    <QrCode className="size-4" />
                    {m.driver_order_pickup_qr()}
                </Button>
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </p>
            ) : cards.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    {m.driver_no_orders()}
                </p>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {cards.map((card) => (
                        <AvailableOrderCard
                            key={`${card.kind}-${card.id}`}
                            card={card}
                        />
                    ))}
                </div>
            )}

            <QRScannerDialog
                open={qrScanOpen}
                onOpenChange={setQrScanOpen}
                onComplete={handlePickupComplete}
            />
        </div>
    )
}
